import type { EntityId } from "@note-app/contracts";

export type StorageErrorCode =
  | "NOT_FOUND"
  | "ALREADY_EXISTS"
  | "OPTIMISTIC_CONCURRENCY"
  | "INVALID_CURSOR"
  | "CONSTRAINT_VIOLATION"
  | "UNSUPPORTED_DATABASE_VERSION"
  | "MIGRATION_HISTORY_INVALID"
  | "READ_ONLY"
  | "PERSISTENCE_FAILED";

export class StorageError extends Error {
  public constructor(
    public readonly code: StorageErrorCode,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = new.target.name;
  }
}

export class EntityNotFoundError extends StorageError {
  public constructor(
    public readonly entityType: string,
    public readonly entityId: EntityId,
  ) {
    super("NOT_FOUND", `${entityType} ${entityId} was not found`);
  }
}

export class EntityAlreadyExistsError extends StorageError {
  public constructor(
    public readonly entityType: string,
    public readonly entityId: EntityId,
  ) {
    super("ALREADY_EXISTS", `${entityType} ${entityId} already exists`);
  }
}

export class OptimisticConcurrencyError extends StorageError {
  public constructor(
    public readonly entityId: EntityId,
    public readonly expectedRevision: number,
    public readonly actualRevision: number,
  ) {
    super(
      "OPTIMISTIC_CONCURRENCY",
      `Revision conflict for ${entityId}: expected ${expectedRevision}, actual ${actualRevision}`,
    );
  }
}

export class InvalidCursorError extends StorageError {
  public constructor(cursor: string) {
    super("INVALID_CURSOR", `Invalid pagination cursor: ${cursor}`);
  }
}

export class ConstraintViolationError extends StorageError {
  public constructor(message: string, options?: ErrorOptions) {
    super("CONSTRAINT_VIOLATION", message, options);
  }
}

export class PersistenceError extends StorageError {
  public constructor(message: string, options?: ErrorOptions) {
    super("PERSISTENCE_FAILED", message, options);
  }
}

/**
 * Raised before migrations or other application writes when a database was
 * created by a newer version of the application.
 */
export class UnsupportedDatabaseVersionError extends StorageError {
  public constructor(
    public readonly actualVersion: number,
    public readonly supportedVersion: number,
  ) {
    super(
      "UNSUPPORTED_DATABASE_VERSION",
      `Database schema version ${actualVersion} is newer than supported version ${supportedVersion}`,
    );
  }
}

export class MigrationHistoryError extends StorageError {
  public constructor(message: string, options?: ErrorOptions) {
    super("MIGRATION_HISTORY_INVALID", message, options);
  }
}

export class ReadOnlyStorageError extends StorageError {
  public constructor(message = "The knowledge base is read-only") {
    super("READ_ONLY", message);
  }
}
